import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Modal, Space, Table, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OrderDetailSectionCard from '../components/OrderDetailSectionCard';
import InvoiceDraftDetail from '../../invoices/components/InvoiceDraftDetail';
import { searchPurchaseInvoiceDrafts } from '../../invoices/api/purchaseInvoiceImportApi';
import type { WorkflowInvoice } from '../../../types/workflowInvoice';
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dateUtils';

interface Props {
  supplierOrderId: string;
  canManage: boolean;
  onChanged: () => unknown | Promise<unknown>;
}

export default function SupplierOrderPurchaseInvoicesSection({ supplierOrderId, canManage, onChanged }: Props) {
  const { t } = useTranslation(['orders', 'invoices', 'common']);
  const key = (name: string) => t(`orders:supplierOrders.finance.${name}`);
  const [rows, setRows] = useState<WorkflowInvoice[]>([]);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [revision, setRevision] = useState(0);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const reload = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => {
    if (!canManage) return;
    let active = true;
    setLoading(true); setFailed(false); setRows([]);
    searchPurchaseInvoiceDrafts({ supplierOrderId, limit: 11, offset: page * 10 })
      .then(value => { if (active) setRows(value); })
      .catch(() => { if (active) setFailed(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [supplierOrderId, canManage, page, revision]);
  const persisted = () => { setPage(0); reload(); void Promise.resolve(onChanged()).catch(() => setFailed(true)); };
  return <OrderDetailSectionCard title={key('invoicesTitle')} defaultOpen
    extra={canManage ? <Button type="primary" icon={<PlusOutlined />} onClick={() => setInvoiceId('new')}>{key('addInvoice')}</Button> : undefined}>
    {!canManage ? <Alert type="info" showIcon message={key('accessHint')} /> : <>
      {failed ? <Alert type="error" showIcon message={key('loadFailed')} action={<Button onClick={reload}>{t('common:refresh')}</Button>} /> : <>
        <Table<WorkflowInvoice> rowKey={row => row.id!} size="middle" loading={loading} dataSource={rows.slice(0, 10)} pagination={false}
          scroll={{ x: 620 }} locale={{ emptyText: key('emptyInvoices') }} columns={[
            { title: key('number'), render: (_, row) => <Button type="link" onClick={() => setInvoiceId(row.id!)}>{row.originalDocumentNumber || key('unnumbered')}</Button> },
            { title: key('date'), render: (_, row) => row.issueDate ? formatDate(row.issueDate) : '—' },
            { title: key('net'), align: 'right', render: (_, row) => row.totalNet == null ? '—' : row.currency ? formatCurrency(row.totalNet, row.currency) : key('unknownCurrency') },
            { title: key('currency'), render: (_, row) => row.currency || '—' },
            { title: key('status'), render: (_, row) => <Tag color={row.status === 'APPROVED' ? 'green' : undefined}>{t(`invoices:workflow.page.status.${['DRAFT', 'EXTRACTED', 'READY'].includes(row.status) ? 'draft' : row.status.toLowerCase()}`)}</Tag> },
            { title: 'PZ', render: (_, row) => <Space direction="vertical">{row.approvalResult?.pzDocuments.filter(pz => pz.sourceSupplierOrderId === supplierOrderId).map(pz => <Link key={pz.id} to={`/app/pz/${pz.id}`}>{pz.pzNumber}</Link>)}</Space> },
          ]} />
        {(page > 0 || rows.length > 10) && <Space style={{ marginTop: 12 }}>
          <Button disabled={loading || page === 0} onClick={() => setPage(value => value - 1)}>{key('previous')}</Button>
          <Button disabled={loading || rows.length <= 10} onClick={() => setPage(value => value + 1)}>{key('next')}</Button>
        </Space>}
      </>}
      <Modal open={invoiceId !== null} title={t('invoices:workflow.flow.title')} footer={null} closable={false} maskClosable={false} keyboard={false} width={960} className="invoice-editor-modal" wrapClassName="invoice-editor-modal-wrap" destroyOnClose>
        {invoiceId && <InvoiceDraftDetail key={`${supplierOrderId}:${invoiceId}`} direction="PURCHASE" embedded={{ invoiceId, supplierOrderId, onClose: () => { setInvoiceId(null); reload(); }, onPersisted: persisted }} />}
      </Modal>
    </>}
  </OrderDetailSectionCard>;
}
